import { from } from 'rxjs'
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
} from 'rxjs/operators'
import {
  errorNotification,
  ProductService,
  ApiKeyService,
  ServicePrincipalService,
} from '~/Services'
import { RootEpic } from '../types'
import { ApiKeyActions } from './slice'

const getProductsEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ApiKeyActions.getProducts.match),
    switchMap(() => {
      return from(
        ProductService.Get.getProducts({ params: { paging: false } }),
      ).pipe(
        map(res => ApiKeyActions.getProductsSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ApiKeyActions.getProductsFail()]
        }),
      )
    }),
  )
}

const getProductClaimsEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ApiKeyActions.getProductClaims.match),
    // mergeMap, not switchMap: CreateApiKey dispatches one getProductClaims
    // per product synchronously in a loop — switchMap would cancel every
    // request but the last one before its response ever arrives.
    mergeMap(action => {
      const { servicePrincipalId, productId, audience } = action.payload
      return from(
        ServicePrincipalService.Get.getClaims(servicePrincipalId, audience),
      ).pipe(
        map(res =>
          ApiKeyActions.getProductClaimsSuccess({
            servicePrincipalId,
            productId,
            claims: res.data.claims,
          }),
        ),
        catchError(error => {
          errorNotification(error)
          return [
            ApiKeyActions.getProductClaimsFail({ servicePrincipalId, productId }),
          ]
        }),
      )
    }),
  )
}

const createApiKeyEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ApiKeyActions.createApiKey.match),
    switchMap(action => {
      const { servicePrincipalId, data, productClaimIds } = action.payload
      return from(
        ApiKeyService.Post.createApiKey(servicePrincipalId, data),
      ).pipe(
        switchMap(res => {
          const created = res.data
          if (!productClaimIds.length) {
            return [ApiKeyActions.createApiKeySuccess(created)]
          }
          return from(
            ApiKeyService.Put.replaceClaims(created.id, productClaimIds),
          ).pipe(
            concatMap(() => [ApiKeyActions.createApiKeySuccess(created)]),
          )
        }),
        catchError(error => {
          errorNotification(error)
          return [ApiKeyActions.createApiKeyFail()]
        }),
      )
    }),
  )
}

const getApiKeysEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ApiKeyActions.getApiKeys.match),
    switchMap(action => {
      const { servicePrincipalId, status } = action.payload
      return from(
        ApiKeyService.Get.getApiKeys(servicePrincipalId, {
          params: { status },
        }),
      ).pipe(
        map(res => ApiKeyActions.getApiKeysSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ApiKeyActions.getApiKeysFail()]
        }),
      )
    }),
  )
}

const revokeApiKeyEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ApiKeyActions.revokeApiKey.match),
    switchMap(action => {
      const { servicePrincipalId, apiKeyId } = action.payload
      return from(ApiKeyService.Post.revoke(apiKeyId)).pipe(
        concatMap(() => [
          ApiKeyActions.revokeApiKeySuccess(),
          ApiKeyActions.getApiKeys({ servicePrincipalId }),
        ]),
        catchError(error => {
          errorNotification(error)
          return [ApiKeyActions.revokeApiKeyFail()]
        }),
      )
    }),
  )
}

const getApiKeyClaimsEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ApiKeyActions.getApiKeyClaims.match),
    switchMap(action => {
      const apiKeyId = action.payload
      return from(ApiKeyService.Get.getClaims(apiKeyId)).pipe(
        map(res => ApiKeyActions.getApiKeyClaimsSuccess(res.data)),
        catchError(error => {
          errorNotification(error)
          return [ApiKeyActions.getApiKeyClaimsFail()]
        }),
      )
    }),
  )
}

const updateApiKeyEpic: RootEpic = action$ => {
  return action$.pipe(
    filter(ApiKeyActions.updateApiKey.match),
    switchMap(action => {
      const { apiKeyId, servicePrincipalId, data, productClaimIds } = action.payload
      return from(ApiKeyService.Patch.updateApiKey(apiKeyId, data)).pipe(
        switchMap(() =>
          // Always replace (even with an empty list) — unlike create, an
          // edit can legitimately clear a key's existing scoped claims.
          from(ApiKeyService.Put.replaceClaims(apiKeyId, productClaimIds)),
        ),
        concatMap(() => [
          ApiKeyActions.updateApiKeySuccess(),
          ApiKeyActions.getApiKeys({ servicePrincipalId }),
        ]),
        catchError(error => {
          errorNotification(error)
          return [ApiKeyActions.updateApiKeyFail()]
        }),
      )
    }),
  )
}

export const ApiKeyEpics = [
  getProductsEpic,
  getProductClaimsEpic,
  createApiKeyEpic,
  getApiKeysEpic,
  revokeApiKeyEpic,
  getApiKeyClaimsEpic,
  updateApiKeyEpic,
]
